export const landingCopy = {
  en: {
    layout: {
      navPlatform: 'Product',
      navPatientPortal: 'Patient Portal',
      navPatientApp: 'Patient App',
      navEmrEhr: 'EMR / EHR',
      navAiCore: 'Features',
      navInvestors: 'Investors',
      goToDashboard: 'Go to Dashboard',
      patientLogin: 'Patient Portal',
      loginRequestAccess: 'Login',
      footer: '© 2026 Elen1 Systems. Dubai.',
    },
    platform: {
      heroLine1: 'Appointments move.',
      heroHighlight: 'Care flows.',
      heroLine3: 'Teams stay in sync.',
      heroSubtitle:
        'Elen1 turns scheduling, intake, reminders, and team handoffs into one calm operating rhythm for busy clinics.',
      heroCta: 'Book a demo',
      heroImageAlt: 'Elen1 scheduling workspace with provider calendar and appointment details',
      trustedLabel: 'Proud Partnerships',
      trustedBanner: 'STRATEGIC PARTNERS COMING SOON',
      comparisonTitle: "Running a clinic shouldn't feel this hard",
      comparisonOldTitle: 'The old way',
      comparisonOldSubtitle: 'Multiple disconnected systems',
      comparisonOldItems: [
        "Juggling tools that don't talk to each other",
        'Manual admin and chasing patients',
        'No visibility across the practice',
        'Growth adds complexity, not control',
      ],
      comparisonNewTitle: 'The Elen1 way',
      comparisonNewSubtitle: 'One platform for the whole clinic',
      comparisonNewItems: [
        'One place for care, operations, and payments',
        'Patient engagement connected to the record',
        'Real-time visibility across your entire team',
        'Scale without adding complexity',
      ],
      schedulingSectionEyebrow: 'Scheduling & Payments',
      schedulingSectionTitle: 'Keep booking, intake, and payment moving without extra admin',
      schedulingSectionBody:
        'Elen1 keeps appointments, intake, billing, and follow-up connected from the moment a patient books.',
      schedulingSectionItems: [
        'Patients can book, change, and confirm appointments through the portal',
        'Forms and consent requests reach patients before they walk in',
        'Deposits, invoices, and payments stay tied to the visit',
        'Openings are offered to the waitlist when cancellations happen',
      ],
      schedulingSectionCta: 'Explore booking and payments',
      schedulingSectionImageAlt: 'Elen1 booking and payment workflow preview',
      operationsSectionEyebrow: 'Operations & Team',
      operationsSectionTitle: 'Reduce the back-office load so care stays front and center',
      operationsSectionBody:
        'Elen1 handles routine admin steps so your team can spend more of the day with patients.',
      operationsSectionItems: [
        'Check-in and documentation move quickly from intake to chart',
        'Ready-to-use templates help teams finish notes faster',
        'Rosters, commissions, and timesheets stay in one workspace',
        'Audit-ready reporting keeps clinic oversight clear',
      ],
      operationsSectionCta: 'Explore operations & team',
      operationsSectionImageAlt: 'Clinic team reviewing operations in Elen1',
      growthSectionEyebrow: 'Growth & Insights',
      growthSectionTitle: 'More patients, higher retention, and full visibility over your revenue',
      growthSectionBody:
        'See exactly how your clinic is performing. Track revenue, retention, and team activity, all in one view.',
      growthSectionItems: [
        'Revenue, retention and KPI reports at a glance',
        'Memberships and packages that grow patient lifetime value',
        'Email, SMS, and campaigns built into the same platform',
        'Loyalty and referral programs that bring patients back',
      ],
      growthSectionCta: 'Explore growth & insights',
      growthSectionImageAlt: 'Clinic growth and insights dashboard preview',
      aiScribeBrand: 'Elen1 AI scribe',
      aiScribeTitlePrefix: 'AI built',
      aiScribeTitleSuffix: 'for everyday clinic work',
      aiScribeCta: 'Try Elen1 AI',
      aiScribeKicker: 'AI Scribe Agent',
      aiScribeCardTitle: 'Turn voice into structured clinical notes',
      aiScribeCardBody:
        'AI listens during your consultation and generates structured treatment notes and consult summaries. You focus on the patient, not the screen.',
      aiScribeListening: 'Elen1 AI is taking notes...',
      roiTitle: 'Built to pay for itself',
      roiSource: 'Illustrative scenarios modeled on common clinic workflows — not measured customer results. Actual outcomes vary by clinic size, staffing, services, and adoption.',
      roiStats: [
        {
          label: 'Reduce admin time',
          title: 'Up to ~40% less time on admin',
          body: 'Scheduling, clinical notes, forms, reminders, and follow-ups move through fewer disconnected handoffs.',
        },
        {
          label: 'Onboarding',
          title: 'Faster staff onboarding',
          body: 'New staff ramp faster with guided workflows, clearer permissions, and one shared operating system.',
        },
        {
          label: 'Patient lifetime value',
          title: 'More repeat patient value',
          body: 'Automated follow-ups, packages, loyalty, and rebooking workflows help keep patients engaged.',
        },
        {
          label: 'ROI',
          title: 'Designed to offset its own cost',
          body: 'Reducing manual work can offset software cost before counting retention, revenue, and throughput gains.',
        },
      ],
      customerStoriesEyebrow: 'Founding clinics',
      customerStoriesTitle: 'Be one of our founding clinics',
      customerStoriesBody:
        "We're onboarding our first group of clinics now and building Elen1 around how they actually run day to day.",
      customerStoriesCta: 'Become a founding clinic',
      customerStoriesPoints: [
        'Work directly with the founding team',
        'Hands-on onboarding and data migration',
        'Shape the roadmap for your specialty',
        'Early access to new capabilities',
      ],
      integrationsEyebrow: 'Integrations',
      integrationsTitle: 'Built to connect with the tools you use',
      integrationsBody:
        'Elen1 is built to connect with the platforms clinics rely on for payments, calendars, messaging, and reporting. Integrations are rolling out over time — tell us which ones matter most for your clinic.',
      integrationsCta: 'Talk to us about integrations',
      practiceTypesTitlePrefix: 'Explore Elen1',
      practiceTypesTitleSuffix: 'by practice type...',
      practiceTypesCta: 'See all specialties',
      practiceTypes: [
        'Med Spa',
        'Physiotherapy',
        'Wellness',
        'GP and Primary Care',
        'Dermatology',
        'IV Drip Clinic',
        'Skin Clinic',
        'Hair Transplant',
        'Cosmetic Surgery',
        'Longevity Clinics',
        'Laser Clinic',
        'Mental health',
      ],
      capabilitiesTitlePrefix: '...or explore everything',
      capabilitiesTitleHighlight: 'Elen1 can do',
      capabilityGroups: [
        {
          title: 'Scheduling & Bookings',
          items: ['Online Booking', 'Calendar', 'Classes', 'Waitlist', 'Mobile App'],
        },
        {
          title: 'Care & Documentation',
          items: ['Digital Forms', 'Labs', 'Prescriptions', 'Before and After Photos', 'Telehealth'],
        },
        {
          title: 'Payments & Operations',
          items: ['Elen1 Pay', 'Quotes', 'Team Management', 'Commissions', 'Inventory and Orders'],
        },
        {
          title: 'Marketing & Growth',
          items: ['Campaigns', 'Lead Management', 'Loyalty and Rewards', 'Membership and Packages', 'Reporting and Analytics'],
        },
      ],
      twinTitleLine1: 'Clinical Care.',
      twinTitleLine2: 'Business Growth.',
      twinBody:
        'Elen1 brings clinical care, business operations, and patient engagement into one place. Fewer tools, less friction, more growth.',
      copilotTitle: 'Care and engagement in one workflow',
      copilotBody: 'Connect charting, visit context, follow-up, messaging, and patient touchpoints without moving between disconnected tools.',
      riskEngineTitle: 'Operations built around growth',
      riskEngineBody: 'Coordinate scheduling, payments, rooms, staff, and patient journeys from one operating layer that helps the practice scale.',
      twinImageAlt: 'Clinic operations workspace',
      genomicsTitleLine1: 'Clinical support',
      genomicsTitleLine2: 'for everyday care.',
      genomicsBody:
        'Bring patient history, medication context, and risk signals together so teams can make better decisions without digging through separate systems.',
      genomicsCta: 'See clinical support',
      genomicsImageAlt: 'Genomic Dashboard',
      architectureTitle: 'Why clinics choose Elen1',
      features: [
        {
          title: 'AI-assisted charting inside the encounter',
          body: 'Providers document faster with contextual summaries, next-step guidance, and patient history already connected to the visit.',
        },
        {
          title: 'Scheduling, intake, and follow-up in one system',
          body: 'Front-desk, clinical, and care-coordination teams work from one operating layer for appointments, reminders, queues, and next actions.',
        },
        {
          title: 'Patient communication tied to the record',
          body: 'Messages, forms, results, and care-plan tasks stay linked to the patient journey instead of being scattered across separate tools.',
        },
        {
          title: 'Billing and payments connected to care',
          body: 'Invoices, balances, deposits, and payment collection sit next to the visit and treatment workflow they belong to.',
        },
        {
          title: 'Multi-site control without extra overhead',
          body: 'Standardize workflows, staff coordination, rooms, permissions, and reporting across growing clinic groups.',
        },
        {
          title: 'Reporting that reflects live operations',
          body: 'Track utilization, throughput, collections, and team performance from the same platform running day-to-day clinic execution.',
        },
      ],
      doctorsTitleLine1: 'Spend less time',
      doctorsTitleLine2: 'on admin work.',
      doctorsBody:
        'Elen1 reduces repetitive documentation and coordination work around care so clinicians can stay focused on the patient and the decision in front of them.',
      doctorsImageAlt: 'Schedule Board',
      contactTitle: 'Talk with the team',
      contactBody: 'We are working with clinics and care organizations that want a more connected operating system.\nGet in touch to see whether Elen1 is a fit.',
      formNamePlaceholder: 'Name',
      formOrgPlaceholder: 'Health System / Organization',
      formEmailPlaceholder: 'Email',
      formSubmit: 'Request Demo',
      patientPortalTitle: 'Patient Portal',
      patientPortalSubtitle: 'One place for appointments, labs, plans, and secure communication.',
      patientPortalIntro:
        'The portal is designed for day-to-day patient workflows: upcoming care, follow-ups, documents, billing visibility, and direct coordination with the care team.',
      patientPortalHighlights: [
        {
          title: 'Self-Service Access',
          body: 'Patients can review care tasks, forms, and records without calling the front desk for routine updates.',
        },
        {
          title: 'Operational Clarity',
          body: 'Appointments, outstanding balances, and active medications are surfaced in a simple, single workspace.',
        },
        {
          title: 'Clinical Continuity',
          body: 'Lab reports and care-plan progress are always visible so patients stay aligned with clinical goals.',
        },
      ],
      patientPortalShowcase: [
        {
          title: 'Home Dashboard',
          body: 'A summary workspace with upcoming visits, pending forms, active medications, lab report count, and care tasks in one view.',
          detail:
            'Patients can quickly identify what needs action today, including unresolved forms and preparation steps before the next appointment.',
          imageAlt: 'Patient portal dashboard with tasks and appointment snapshot',
        },
        {
          title: 'Appointments Center',
          body: 'Patients can track upcoming and recent visits, check statuses, and access quick actions like joining virtual visits or messaging care teams.',
          detail:
            'Status chips and action controls reduce missed visits by showing exactly when to check in, who the provider is, and what to do next.',
          imageAlt: 'Patient portal appointments list and action panel',
        },
        {
          title: 'Lab Results View',
          body: 'Recent panels are organized by collection date, ordering provider, and abnormal values to make follow-up decisions easier.',
          detail:
            'Flagged values are visible without opening separate reports, helping patients and clinicians prioritize follow-up conversations faster.',
          imageAlt: 'Patient portal lab results table with abnormal flags',
        },
        {
          title: 'Care Plan Tracking',
          body: 'Progress bars and next-review dates help patients understand adherence and when intervention milestones need attention.',
          detail:
            'Longitudinal plan visibility keeps patients engaged between visits and supports shared accountability for chronic condition goals.',
          imageAlt: 'Patient portal care plan cards with progress indicators',
        },
      ],
      patientAppTitle: 'Patient App',
      patientAppSubtitle: 'Mobile app coming soon for patients and families.',
      patientAppBadge: 'iOS App Coming Soon',
      patientAppIntro:
        'The upcoming patient app extends the portal experience to mobile with real-time notifications, easier follow-up, and always-on access to care workflows.',
      patientAppImageAlt: 'Upcoming Elen1 patient mobile app preview',
      patientAppLaunchEyebrow: 'iOS Patient App',
      patientAppLaunchTitle: 'Coming Soon',
      patientAppLaunchDate: 'Early access opening in 2026',
      patientAppLaunchPrimaryCta: 'Join Waitlist',
      patientAppLaunchSecondaryCta: 'See Features',
      patientAppFeatures: [
        {
          title: 'Telemedicine on Mobile',
          body: 'Join scheduled video visits with one tap and receive check-in reminders before the consult starts.',
        },
        {
          title: 'Secure Care-Team Messaging',
          body: 'Continue conversations with providers and care coordinators directly from the app.',
        },
        {
          title: 'Lab Results & Medical Records',
          body: 'Review new lab reports, historical records, and care-plan updates without waiting for phone callbacks.',
        },
        {
          title: 'Appointments & Smart Reminders',
          body: 'Track upcoming visits, prep tasks, and status changes with push notifications.',
        },
        {
          title: 'Medication & Care Tasks',
          body: 'Follow medications, daily tasks, and plan milestones in a simple timeline designed for adherence.',
        },
        {
          title: 'Billing & Documents',
          body: 'Access invoices, payment status, and requested forms from one mobile workspace.',
        },
      ],
      emrEhrTitle: 'EMR / EHR',
      emrEhrSubtitle:
        'A full-stack clinical operating system for care delivery, triage, diagnostics, communication, and revenue workflows.',
      emrEhrIntro:
        'The EMR/EHR workspace unifies front-desk intake, provider workflows, AI-assisted decision support, patient communication, lab operations, and billing execution in one connected platform.',
      emrEhrHighlights: [
        {
          title: 'End-to-End Clinical Workflow',
          body: 'From patient registration to follow-up and billing, teams work in one operational surface with shared context.',
        },
        {
          title: 'AI-Assisted Operations',
          body: 'Risk signals, triage summaries, and longitudinal clinical snapshots are embedded directly in day-to-day workflows.',
        },
        {
          title: 'Cross-Module Visibility',
          body: 'Operations, population health, care coordination, and reports stay synchronized to reduce blind spots.',
        },
      ],
      emrEhrShowcase: [
        {
          title: 'Operations Dashboard',
          body: 'Leadership sees AI-generated status summaries, core KPI trends, and appointment load snapshots for daily operations.',
          detail:
            'The top-level overview highlights pressure points early, so operations teams can rebalance staffing and throughput before delays cascade.',
          imageAlt: 'EMR operations dashboard with AI overview, KPI cards, and trend charts',
        },
        {
          title: 'Patient Registry',
          body: 'The patient list supports filtering, status management, care-team assignment, and rapid actions from one table view.',
          detail:
            'Teams can segment by provider, status, and demographics to triage outreach queues and reduce administrative back-and-forth.',
          imageAlt: 'Patient registry table with filters, statuses, and quick actions',
        },
        {
          title: 'Patient File with AI Summary',
          body: 'Each chart includes a longitudinal AI summary with risk factors, care gaps, medications, timeline, and active follow-up items.',
          detail:
            'Clinicians get a concise narrative of what changed, what remains unresolved, and where risk is accumulating across encounters.',
          imageAlt: 'Patient chart with longitudinal AI summary, risks, and care timeline',
        },
        {
          title: 'Clinical Messaging Workspace',
          body: 'Secure inbox-style messaging enables foldering, thread triage, patient tagging, and fast care-team communication.',
          detail:
            'Conversation context stays linked to patient records, improving handoffs and reducing duplicated communication across teams.',
          imageAlt: 'Clinical messaging inbox with thread view and tagged patient participant',
        },
        {
          title: 'Walk-In and Urgent Care Triage',
          body: 'Urgency scoring and AI summaries help staff prioritize arrivals and standardize next-step recommendations in real time.',
          detail:
            'Structured triage guidance supports safer intake decisions during high-volume periods and improves consistency across staff.',
          imageAlt: 'Urgent care queue with AI triage urgency levels and recommended steps',
        },
        {
          title: 'Lab Reports and Results Workflow',
          body: 'HL7 ingestion, abnormal-value detection, and acknowledgement steps are managed in a structured clinical workflow.',
          detail:
            'Inbox review, critical-value flags, and acknowledgement actions create auditable loops for lab follow-up and escalation.',
          imageAlt: 'Lab reports module with inbox, abnormal flags, and selected result details',
        },
        {
          title: 'Billing Workspace',
          body: 'Invoice creation, line-item configuration, totals, and outstanding balances are handled directly from the patient billing view.',
          detail:
            'Clinical and billing teams share the same patient context, which helps reduce claim friction and accelerates revenue cycle completion.',
          imageAlt: 'Patient billing workspace with invoice builder and billing catalog',
        },
        {
          title: 'Population Health Intelligence',
          body: 'Teams can track alert volumes, case clusters, and geo-spatial concentration to guide interventions and capacity planning.',
          detail:
            'Spatial and cohort-level visibility supports targeted campaigns, smarter clinic load balancing, and higher-impact preventive outreach.',
          imageAlt: 'Population health dashboard with map-based distribution and alert metrics',
        },
        {
          title: 'Custom Report Builder',
          body: 'Operations teams can configure datasets, filters, groupings, schedules, and exports to run repeatable analytics workflows.',
          detail:
            'Report templates and scheduled execution make analytics reproducible for leadership, quality initiatives, and compliance reviews.',
          imageAlt: 'Custom report builder with filters, grouping fields, and execution options',
        },
      ],
      emptyPageBody: 'Coming soon.',
    },
    aiCore: {
      headerTitle: 'Features',
      headerBody: 'Everything Elen1 runs inside one connected clinical operating system.',
      videoSubtitle: 'Healthcare deserves better',
      featuresTitle: 'AI & Core Clinical Features',
      featuresIntro:
        'Each feature below works inside the same patient and operational context so teams can move through daily work without jumping between separate tools.',
      features: [
        {
          title: 'Clinical Command Center',
          body:
            'The Command Center gives clinical and operations leaders a live, unified view of what is happening across the organization: patient flow, provider load, pending tasks, risk hotspots, and financial pressure points.',
          detail:
            'Instead of switching across disconnected dashboards, teams can coordinate decisions in one place and execute interventions before delays affect quality of care.',
          highlights: [
            {
              title: 'Unified Visibility',
              body: 'Combines scheduling, care delivery, documentation status, and billing signals into one real-time operational view.',
            },
            {
              title: 'Action-Oriented Workflow',
              body: 'Critical signals are tied to immediate next steps, so staff can resolve bottlenecks directly from the same screen.',
            },
          ],
          image: '/newfeatures/dashboard.png',
          imageAlt: 'Elen1 dashboard with operational and clinical intelligence',
        },
        {
          title: 'Sentinel AI Agent Inside the EMR',
          body:
            'Sentinel is not a separate tool. It lives inside the EMR and continuously evaluates chart context, active conditions, current medications, recent events, and incoming clinical data.',
          detail:
            'Clinicians receive context-aware recommendations while reviewing the patient, with full continuity between AI guidance and charting workflows.',
          highlights: [
            {
              title: 'Context-Aware Guidance',
              body: 'Recommendations are generated using the full patient timeline, not isolated single-visit information.',
            },
            {
              title: 'Workflow-Native AI',
              body: 'Suggestions appear directly where clinicians document and place orders, minimizing interruptions and cognitive load.',
            },
          ],
          image: '/newfeatures/sentinel-ai-agent-living-inside-emr.png',
          imageAlt: 'Sentinel AI agent panel embedded in the EMR workflow',
        },
        {
          title: 'Automatic Triage AI Agent',
          body:
            'The triage agent automatically stratifies incoming patients by urgency, suspected condition severity, and predicted risk of deterioration before full physician review.',
          detail:
            'This allows front-desk, nursing, and urgent care teams to route critical patients faster and reduce preventable waiting-room escalation.',
          highlights: [
            {
              title: 'Early Acuity Detection',
              body: 'Identifies high-risk arrivals early using symptoms, intake history, vitals, and prior episodes.',
            },
            {
              title: 'Smarter Queue Management',
              body: 'Reorders queues dynamically as new information arrives, so the right patient is seen at the right time.',
            },
          ],
          image: '/newfeatures/automatic-triage-ai-agent.png',
          imageAlt: 'Automatic triage AI agent prioritizing patients by urgency',
        },
        {
          title: 'AI-Generated Patient Summary',
          body:
            'Patient details include an auto-generated summary that synthesizes longitudinal history, active diagnoses, medications, allergies, unresolved tasks, and risk trajectories.',
          detail:
            'The result is a concise but high-fidelity clinical briefing that helps teams understand patient status in seconds without losing important nuance.',
          highlights: [
            {
              title: 'Timeline Compression',
              body: 'Transforms long records into a structured narrative focused on what has changed and what now needs attention.',
            },
            {
              title: 'Decision-Ready Context',
              body: 'Highlights key risk trends and unresolved clinical items to speed safe decision-making at the point of care.',
            },
          ],
          image: '/newfeatures/patient-details-autogenerated-ai-summary.png',
          imageAlt: 'Patient detail page with auto-generated AI summary',
        },
        {
          title: 'Lab Results Intelligence',
          body:
            'As lab results arrive, Elen1 interprets values in clinical context rather than as isolated numbers, surfacing abnormal trajectories and potentially significant combinations.',
          detail:
            'Clinicians can see what changed, why it matters now, and which follow-up actions are likely to be relevant without manually cross-referencing multiple modules.',
          highlights: [
            {
              title: 'Trend-Aware Interpretation',
              body: 'Detects concerning drifts over time and distinguishes acute shifts from expected variation.',
            },
            {
              title: 'Immediate Clinical Context',
              body: 'Links lab abnormalities to active diagnoses, treatments, and recent events to reduce missed signals.',
            },
          ],
          image: '/newfeatures/lab-results.png',
          imageAlt: 'Lab results module with AI interpretation and abnormal trend detection',
        },
        {
          title: 'Telemedicine Workspace',
          body:
            'The telemedicine workspace combines virtual visits, chart access, order entry, and documentation in one continuous interface built for remote-first care delivery.',
          detail:
            'Providers can conduct, document, and complete visits without toggling tools, improving visit quality while reducing administrative overhead.',
          highlights: [
            {
              title: 'End-to-End Virtual Workflow',
              body: 'Consultation, clinical review, documentation, and order management are all completed in a single visit surface.',
            },
            {
              title: 'Lower Cognitive Friction',
              body: 'Eliminates app switching during encounters so clinicians stay focused on patient interaction and decision quality.',
            },
          ],
          image: '/newfeatures/telemedicine.png',
          imageAlt: 'Telemedicine workflow integrated with patient chart',
        },
        {
          title: 'Live Diagnostics During Telemedicine',
          body:
            'During active video encounters, Elen1 produces real-time diagnostic support and evolving encounter summaries as new clinical information is discussed.',
          detail:
            'This enables clinicians to validate impressions, document findings, and finalize decisions while the consultation is still in progress.',
          highlights: [
            {
              title: 'In-Visit Clinical Support',
              body: 'Generates structured diagnostic suggestions and follow-up considerations as the conversation unfolds.',
            },
            {
              title: 'Concurrent Documentation',
              body: 'Builds visit summaries in real time so the chart is substantially complete before the call ends.',
            },
          ],
          image: '/newfeatures/telemedicine-ai-summary-live-diagnostics.png',
          imageAlt: 'Telemedicine session with live AI diagnostics and summary',
        },
      ],
      contactTitle: 'See the Core in Action',
      contactBody: 'Full technical whitepapers and API documentation available upon request.',
      contactEmailPlaceholder: 'Institutional Email',
      contactSubmit: 'Request Technical Briefing',
    },
    investors: {
      heroLine1: 'Invest in the',
      heroHighlight: 'Next Era',
      heroLine3: 'of Clinic Software.',
      heroSubtitle:
        'Elen1 is building clinic software that keeps scheduling, patient access, billing, telemedicine, and clinical operations in one system.',
      heroCta: 'Inquire About Allocation',
      heroImageAlt: 'Growth Chart and Schedule Board',
      shiftTitle: 'The $30 Trillion Shift',
      feature1Title: 'Regional expansion',
      feature1Body:
        'We are building for clinics across Europe, Latin America, and Canada with a product that can adapt to local operating and compliance needs.',
      feature2Title: 'Product depth',
      feature2Body:
        'The product gets stronger as we add more workflow coverage, clearer implementation patterns, and better operational data across the clinics we support.',
      feature3Title: 'Compliance readiness',
      feature3Body:
        'Elen1 is being built with privacy, security, and AI-governance expectations in mind across the markets we plan to serve.',
      leadershipTitle: 'Leadership Team',
      leadershipSubtitle: 'Experienced founders with a track record in healthcare and fintech.',
      ceoRole: 'Founder & CEO',
      ceoBio:
        'Serial Entrepreneur. Ex-Medesync and Telus Health. Led digital transformation for major global banks.',
      ctoRole: 'Co-Founder & CTO',
      ctoBio:
        'Former Engineering Lead at PayPal. Architect of secure, high-transaction financial infrastructure.',
      cooRole: 'Co-Founder & COO',
      cooBio:
        'Former Medtronic Executive and prominent Dubai Angel Investor. Strategic force in global health scaling.',
      roundTitle: 'Speak with the team',
      roundBody: 'We are in active conversations with investors and strategic partners who understand healthcare operations and long-term product building.',
      formFundPlaceholder: 'Fund / Firm Name',
      formCheckSizePlaceholder: 'Check Size Interest',
      formEmailPlaceholder: 'Partner Email',
      formSubmit: 'Start the conversation',
    },
  },
  es: {
    layout: {
      navPlatform: 'Producto',
      navPatientPortal: 'Portal del Paciente',
      navPatientApp: 'App del Paciente',
      navEmrEhr: 'EMR / HCE',
      navAiCore: 'Funciones',
      navInvestors: 'Inversionistas',
      goToDashboard: 'Ir al Panel',
      patientLogin: 'Portal Paciente',
      loginRequestAccess: 'Iniciar sesión',
      footer: '© 2026 Elen1 Systems. Dubái.',
    },
    platform: {
      heroLine1: 'La agenda avanza.',
      heroHighlight: 'La atención fluye.',
      heroLine3: 'El equipo se sincroniza.',
      heroSubtitle:
        'Elen1 convierte agenda, admisión, recordatorios y traspasos del equipo en un ritmo operativo claro para clínicas ocupadas.',
      heroCta: 'Reservar demo',
      heroImageAlt: 'Espacio de agenda de Elen1 con calendario de proveedores y detalles de citas',
      trustedLabel: 'Alianzas de las que estamos orgullosos',
      trustedBanner: 'ALIADOS ESTRATÉGICOS PRÓXIMAMENTE',
      comparisonTitle: 'Dirigir una clínica no debería sentirse tan difícil',
      comparisonOldTitle: 'La forma anterior',
      comparisonOldSubtitle: 'Múltiples sistemas desconectados',
      comparisonOldItems: [
        'Herramientas que no se comunican entre sí',
        'Administración manual y seguimiento constante',
        'Poca visibilidad en toda la clínica',
        'Crecer suma complejidad, no control',
      ],
      comparisonNewTitle: 'La forma Elen1',
      comparisonNewSubtitle: 'Una plataforma para toda la clínica',
      comparisonNewItems: [
        'Un lugar para atención, operaciones y pagos',
        'Participación del paciente conectada al expediente',
        'Visibilidad en tiempo real para todo el equipo',
        'Escala sin añadir complejidad',
      ],
      schedulingSectionEyebrow: 'Agenda y pagos',
      schedulingSectionTitle: 'Desde la primera reserva hasta el pago final, sin carga administrativa',
      schedulingSectionBody:
        'Desde la primera cita hasta el pago final, cada paso permanece conectado en un solo flujo de Elen1.',
      schedulingSectionItems: [
        'Reserva y reprogramación en segundos desde el portal del paciente',
        'Formularios digitales y consentimientos enviados antes de la llegada',
        'Pagos, depósitos e invoices gestionados en un solo lugar',
        'La lista de espera rellena cancelaciones automáticamente',
      ],
      schedulingSectionCta: 'Explorar agenda y pagos',
      schedulingSectionImageAlt: 'Vista previa del flujo de reservas y pagos de Elen1',
      operationsSectionEyebrow: 'Operaciones y equipo',
      operationsSectionTitle: 'Menos tiempo en papeleo. Más tiempo para pacientes',
      operationsSectionBody:
        'Automatiza el trabajo repetitivo para que tu equipo se enfoque en lo importante: los pacientes.',
      operationsSectionItems: [
        'Pacientes registrados y documentados en minutos',
        'Plantillas preconfiguradas que reducen el tiempo de documentación',
        'Turnos, comisiones y hojas de tiempo del personal en un solo lugar',
        'Cumplimiento y reportes para mantener el control',
      ],
      operationsSectionCta: 'Explorar operaciones y equipo',
      operationsSectionImageAlt: 'Equipo clínico revisando operaciones en Elen1',
      growthSectionEyebrow: 'Crecimiento e insights',
      growthSectionTitle: 'Más pacientes, mayor retención y visibilidad completa de tus ingresos',
      growthSectionBody:
        'Mira exactamente cómo está funcionando tu clínica. Sigue ingresos, retención y actividad del equipo en una sola vista.',
      growthSectionItems: [
        'Reportes de ingresos, retención y KPIs de un vistazo',
        'Membresías y paquetes que aumentan el valor de vida del paciente',
        'Email, SMS y campañas dentro de la misma plataforma',
        'Programas de lealtad y referidos que hacen volver a los pacientes',
      ],
      growthSectionCta: 'Explorar crecimiento e insights',
      growthSectionImageAlt: 'Vista previa de crecimiento e insights de clínica',
      aiScribeBrand: 'Elen1 AI scribe',
      aiScribeTitlePrefix: 'IA creada',
      aiScribeTitleSuffix: 'para el trabajo diario de la clínica',
      aiScribeCta: 'Probar Elen1 AI',
      aiScribeKicker: 'Agente AI Scribe',
      aiScribeCardTitle: 'Convierte voz en notas clínicas estructuradas',
      aiScribeCardBody:
        'La IA escucha durante la consulta y genera notas de tratamiento y resúmenes estructurados. Tú te enfocas en el paciente, no en la pantalla.',
      aiScribeListening: 'Elen1 AI está tomando notas...',
      roiTitle: 'Diseñado para pagarse solo',
      roiSource: 'Escenarios ilustrativos modelados con flujos comunes de clínicas, no resultados medidos de clientes. Los resultados reales varían según tamaño, equipo, servicios y adopción.',
      roiStats: [
        {
          label: 'Reducir admin',
          title: 'Hasta ~40% menos tiempo en administración',
          body: 'Agenda, notas clínicas, formularios, recordatorios y seguimiento pasan por menos traspasos desconectados.',
        },
        {
          label: 'Onboarding',
          title: 'Incorporación de equipo más rápida',
          body: 'El personal nuevo avanza más rápido con flujos guiados, permisos claros y un sistema operativo compartido.',
        },
        {
          label: 'Valor del paciente',
          title: 'Más valor en pacientes recurrentes',
          body: 'Seguimientos automáticos, paquetes, lealtad y rebooking ayudan a mantener pacientes activos.',
        },
        {
          label: 'ROI',
          title: 'Pensado para cubrir su propio costo',
          body: 'Reducir trabajo manual puede compensar el costo del software antes de contar retención, ingresos y capacidad.',
        },
      ],
      customerStoriesEyebrow: 'Clínicas fundadoras',
      customerStoriesTitle: 'Sé una de nuestras clínicas fundadoras',
      customerStoriesBody:
        'Estamos incorporando a nuestro primer grupo de clínicas y construyendo Elen1 en torno a cómo operan realmente cada día.',
      customerStoriesCta: 'Ser clínica fundadora',
      customerStoriesPoints: [
        'Trabaja directamente con el equipo fundador',
        'Onboarding y migración de datos acompañados',
        'Influye en el roadmap para tu especialidad',
        'Acceso anticipado a nuevas funciones',
      ],
      integrationsEyebrow: 'Integraciones',
      integrationsTitle: 'Diseñado para conectarse con tus herramientas',
      integrationsBody:
        'Elen1 está diseñado para conectarse con las plataformas que las clínicas usan para pagos, calendarios, mensajería y reportes. Las integraciones se irán habilitando con el tiempo: cuéntanos cuáles te importan más.',
      integrationsCta: 'Hablemos de integraciones',
      practiceTypesTitlePrefix: 'Explora Elen1',
      practiceTypesTitleSuffix: 'por tipo de clínica...',
      practiceTypesCta: 'Ver especialidades',
      practiceTypes: [
        'Med Spa',
        'Fisioterapia',
        'Wellness',
        'Medicina general',
        'Dermatología',
        'Clínica IV Drip',
        'Clínica de piel',
        'Trasplante capilar',
        'Cirugía estética',
        'Clínicas de longevidad',
        'Clínica láser',
        'Salud mental',
      ],
      capabilitiesTitlePrefix: '...o explora todo lo que',
      capabilitiesTitleHighlight: 'Elen1 puede hacer',
      capabilityGroups: [
        {
          title: 'Agenda y reservas',
          items: ['Reserva online', 'Calendario', 'Clases', 'Lista de espera', 'App móvil'],
        },
        {
          title: 'Atención y documentación',
          items: ['Formularios digitales', 'Labs', 'Prescripciones', 'Fotos antes y después', 'Telehealth'],
        },
        {
          title: 'Pagos y operaciones',
          items: ['Elen1 Pay', 'Cotizaciones', 'Gestión de equipo', 'Comisiones', 'Inventario y pedidos'],
        },
        {
          title: 'Marketing y crecimiento',
          items: ['Campañas', 'Gestión de leads', 'Lealtad y recompensas', 'Membresías y paquetes', 'Reportes y analítica'],
        },
      ],
      twinTitleLine1: 'Un Solo Espacio.',
      twinTitleLine2: 'Hecho para Clínicas Reales.',
      twinBody:
        'Elen1 integra documentación, agenda, comunicación con pacientes, pagos y coordinación operativa en un solo flujo para que el equipo avance sin depender de sistemas desconectados.',
      copilotTitle: 'Documentación al ritmo de la atención',
      copilotBody: 'Captura contexto clínico, resúmenes de visita y próximas acciones directamente dentro del trabajo diario del profesional.',
      riskEngineTitle: 'Claridad operativa para toda la clínica',
      riskEngineBody: 'Muestra qué está programado, pendiente, atrasado o bloqueado entre proveedores, salas y recorridos del paciente.',
      twinImageAlt: 'Espacio de operaciones clínicas',
      genomicsTitleLine1: 'Inteligencia de Flujo Clínico',
      genomicsTitleLine2: 'para la Atención Diaria.',
      genomicsBody:
        'La medicina de precisión ya no es una especialidad: es el estándar. Nuestra Capa de Inteligencia Genómica permite verificaciones farmacogenómicas en tiempo real y predicción longitudinal de enfermedades basada en el perfil molecular único del paciente.',
      genomicsCta: 'Ver Planificador de Tratamiento de Precisión',
      genomicsImageAlt: 'Panel Genómico',
      architectureTitle: 'Por Qué Elen1 Funciona para Clínicas Modernas',
      features: [
        {
          title: 'Documentación asistida por IA dentro de la consulta',
          body: 'Los profesionales documentan más rápido con resúmenes contextuales, guía de siguiente paso e historial del paciente conectado a la visita.',
        },
        {
          title: 'Agenda, admisión y seguimiento en un solo sistema',
          body: 'Recepción, equipo clínico y coordinación trabajan desde una sola capa operativa para citas, recordatorios, colas y próximas acciones.',
        },
        {
          title: 'Comunicación con pacientes unida al expediente',
          body: 'Mensajes, formularios, resultados y tareas del plan de cuidado permanecen ligados al recorrido del paciente en vez de dispersarse entre herramientas.',
        },
        {
          title: 'Facturación y pagos conectados con la atención',
          body: 'Facturas, saldos, depósitos y cobros viven junto al flujo real de visita y tratamiento al que pertenecen.',
        },
        {
          title: 'Control multi-sede sin sobrecarga adicional',
          body: 'Estandariza flujos, coordinación de personal, salas, permisos y reportes para grupos de clínicas en crecimiento.',
        },
        {
          title: 'Reportes que reflejan la operación en vivo',
          body: 'Sigue utilización, productividad, cobros y desempeño del equipo desde la misma plataforma que ejecuta la operación diaria.',
        },
      ],
      doctorsTitleLine1: 'Recupera el',
      doctorsTitleLine2: 'Arte de la Medicina.',
      doctorsBody:
        'Elen1 automatiza la burocracia del cuidado. Desde la orquestación asistencial con IA hasta el diagnóstico diferencial con IA, nosotros gestionamos los datos para que tú gestiones al paciente.',
      doctorsImageAlt: 'Panel de Agenda',
      contactTitle: 'Únete a la Red de Inteligencia',
      contactBody:
        'Elen1 se está desplegando actualmente en centros médicos académicos seleccionados.\nConsulta por alianzas.',
      formNamePlaceholder: 'Nombre',
      formOrgPlaceholder: 'Sistema de Salud / Organización',
      formEmailPlaceholder: 'Correo electrónico',
      formSubmit: 'Solicitar Demo',
      patientPortalTitle: 'Portal del Paciente',
      patientPortalSubtitle: 'Un solo lugar para citas, laboratorios, planes y comunicación segura.',
      patientPortalIntro:
        'El portal está diseñado para los flujos diarios del paciente: próximos cuidados, seguimientos, documentos, visibilidad de facturación y coordinación directa con el equipo clínico.',
      patientPortalHighlights: [
        {
          title: 'Acceso de Autoservicio',
          body: 'Los pacientes pueden revisar tareas, formularios y registros sin depender de llamadas para actualizaciones rutinarias.',
        },
        {
          title: 'Claridad Operativa',
          body: 'Citas, saldos pendientes y medicación activa se muestran en un único espacio de trabajo simple.',
        },
        {
          title: 'Continuidad Clínica',
          body: 'Los reportes de laboratorio y el avance del plan de cuidado se mantienen visibles para alinear objetivos clínicos.',
        },
      ],
      patientPortalShowcase: [
        {
          title: 'Panel Principal',
          body: 'Un espacio de resumen con próximas visitas, formularios pendientes, medicamentos activos, reportes de laboratorio y tareas de cuidado.',
          detail:
            'El paciente identifica rapido que requiere accion hoy, incluyendo pendientes administrativos y preparacion para su siguiente cita.',
          imageAlt: 'Panel del portal del paciente con tareas y resumen de citas',
        },
        {
          title: 'Centro de Citas',
          body: 'Los pacientes pueden seguir citas próximas y recientes, ver estados y usar acciones rápidas como entrar a teleconsulta o enviar mensajes.',
          detail:
            'Los estados y accesos directos reducen inasistencias al mostrar hora, profesional y paso siguiente en un solo lugar.',
          imageAlt: 'Lista de citas del portal del paciente con panel de acciones',
        },
        {
          title: 'Vista de Laboratorios',
          body: 'Los paneles recientes se organizan por fecha de toma, profesional solicitante y valores anormales para facilitar el seguimiento.',
          detail:
            'Los valores marcados se ven sin abrir cada reporte, lo que acelera decisiones de seguimiento entre paciente y equipo clinico.',
          imageAlt: 'Tabla de resultados de laboratorio del portal del paciente con banderas anormales',
        },
        {
          title: 'Seguimiento del Plan de Cuidado',
          body: 'Barras de progreso y próximas fechas de revisión ayudan a entender adherencia y cuándo atender hitos clínicos.',
          detail:
            'La visibilidad longitudinal del plan mantiene al paciente comprometido entre consultas y mejora la adherencia terapeutica.',
          imageAlt: 'Tarjetas de planes de cuidado del portal del paciente con indicadores de progreso',
        },
      ],
      patientAppTitle: 'App del Paciente',
      patientAppSubtitle: 'Aplicación móvil próximamente para pacientes y familias.',
      patientAppBadge: 'App iOS Próximamente',
      patientAppIntro:
        'La próxima app del paciente extiende la experiencia del portal al móvil con notificaciones en tiempo real, seguimiento más simple y acceso continuo al cuidado.',
      patientAppImageAlt: 'Vista previa de la próxima app móvil de pacientes Elen1',
      patientAppLaunchEyebrow: 'App iOS para Pacientes',
      patientAppLaunchTitle: 'Próximamente',
      patientAppLaunchDate: 'Acceso anticipado en 2026',
      patientAppLaunchPrimaryCta: 'Unirme a la lista',
      patientAppLaunchSecondaryCta: 'Ver funciones',
      patientAppFeatures: [
        {
          title: 'Telemedicina en Móvil',
          body: 'Únete a videoconsultas programadas en un toque y recibe recordatorios de check-in antes de iniciar.',
        },
        {
          title: 'Mensajería Segura con el Equipo',
          body: 'Continúa conversaciones con profesionales y coordinadores de cuidado directamente desde la app.',
        },
        {
          title: 'Laboratorios y Expediente Clínico',
          body: 'Revisa nuevos reportes de laboratorio, historial clínico y cambios del plan sin esperar llamadas.',
        },
        {
          title: 'Citas y Recordatorios Inteligentes',
          body: 'Da seguimiento a próximas citas, tareas previas y cambios de estado con notificaciones push.',
        },
        {
          title: 'Medicaciones y Tareas de Cuidado',
          body: 'Sigue medicamentos, tareas diarias e hitos del plan en una línea de tiempo clara para adherencia.',
        },
        {
          title: 'Facturación y Documentos',
          body: 'Consulta facturas, estado de pago y formularios solicitados en un solo espacio móvil.',
        },
      ],
      emrEhrTitle: 'EMR / HCE',
      emrEhrSubtitle:
        'Un sistema operativo clínico integral para atención, triaje, diagnósticos, comunicación y flujos financieros.',
      emrEhrIntro:
        'El espacio EMR/HCE unifica admisión, flujos del profesional, soporte clínico con IA, comunicación con pacientes, operaciones de laboratorio, monitoreo remoto y ejecución de facturación en una sola plataforma conectada.',
      emrEhrHighlights: [
        {
          title: 'Flujo Clínico de Extremo a Extremo',
          body: 'Desde el registro del paciente hasta el seguimiento y la facturación, todo se gestiona en una misma superficie operativa.',
        },
        {
          title: 'Operación Asistida por IA',
          body: 'Señales de riesgo, resúmenes de triaje y contexto clínico longitudinal aparecen integrados en el flujo diario.',
        },
        {
          title: 'Visibilidad Entre Módulos',
          body: 'Operaciones, salud poblacional, monitoreo remoto y reportes se mantienen sincronizados para reducir puntos ciegos.',
        },
      ],
      emrEhrShowcase: [
        {
          title: 'Panel de Operaciones',
          body: 'Liderazgo visualiza resúmenes de estado generados por IA, tendencias de KPI y carga de citas para la operación diaria.',
          detail:
            'La vista ejecutiva detecta cuellos de botella temprano para ajustar capacidad, agendas y prioridades antes de afectar la atencion.',
          imageAlt: 'Panel operativo del EMR con resumen de IA, KPI y gráficos de tendencia',
        },
        {
          title: 'Registro de Pacientes',
          body: 'La lista de pacientes permite filtrar, gestionar estados, asignar responsables y ejecutar acciones rápidas en una sola tabla.',
          detail:
            'Los equipos segmentan por estado, proveedor y perfil clinico para ordenar trabajo pendiente y acelerar seguimiento.',
          imageAlt: 'Tabla de pacientes con filtros, estados y acciones rápidas',
        },
        {
          title: 'Expediente con Resumen de IA',
          body: 'Cada expediente incluye un resumen longitudinal de IA con factores de riesgo, brechas de cuidado, medicación y línea de tiempo.',
          detail:
            'El profesional recibe un contexto clinico sintetizado de cambios recientes, pendientes y riesgos acumulados del paciente.',
          imageAlt: 'Expediente clínico con resumen longitudinal de IA, riesgos y cronología',
        },
        {
          title: 'Mensajería Clínica',
          body: 'La mensajería segura tipo inbox permite carpetas, triaje de hilos, etiquetado de pacientes y coordinación rápida del equipo.',
          detail:
            'La conversacion se mantiene ligada al paciente, mejorando traspasos y evitando duplicidad de mensajes entre areas.',
          imageAlt: 'Bandeja de mensajería clínica con hilo activo y paciente etiquetado',
        },
        {
          title: 'Triaje de Urgencias y Walk-In',
          body: 'La puntuación de urgencia y los resúmenes de IA ayudan a priorizar llegadas y estandarizar próximos pasos en tiempo real.',
          detail:
            'El triaje estructurado reduce variabilidad en admision y permite escalar casos criticos con mayor seguridad operacional.',
          imageAlt: 'Cola de urgencias con niveles de triaje IA y recomendaciones',
        },
        {
          title: 'Hub de Monitoreo Remoto',
          body: 'Las señales de dispositivos en vivo muestran desvíos de riesgo, nivel de confianza y acciones sugeridas para seguimiento proactivo.',
          detail:
            'Las alertas priorizadas convierten telemetria en tareas accionables para intervencion temprana y continuidad asistencial.',
          imageAlt: 'Tabla de monitoreo remoto con desviaciones de biomarcadores y puntajes de riesgo',
        },
        {
          title: 'Flujo de Resultados de Laboratorio',
          body: 'La ingesta HL7, detección de valores anormales y pasos de validación se gestionan con un flujo clínico estructurado.',
          detail:
            'La bandeja de resultados, las banderas criticas y la trazabilidad de acciones fortalecen el cierre de circuito diagnostico.',
          imageAlt: 'Módulo de laboratorios con inbox, banderas anormales y detalle del resultado',
        },
        {
          title: 'Espacio de Facturación',
          body: 'La creación de facturas, configuración de conceptos, totales y saldos pendientes se realiza directamente desde la vista del paciente.',
          detail:
            'Contexto clinico y financiero compartido reduce friccion de cobro y mejora la velocidad del ciclo de ingresos.',
          imageAlt: 'Espacio de facturación del paciente con constructor de factura y catálogo',
        },
        {
          title: 'Inteligencia de Salud Poblacional',
          body: 'El equipo monitorea alertas, clústeres de casos y concentración geográfica para dirigir intervenciones y capacidad.',
          detail:
            'La vista geoespacial ayuda a definir campanas focalizadas y decisiones de capacidad basadas en riesgo real de la poblacion.',
          imageAlt: 'Panel de salud poblacional con mapa de distribución y métricas de alertas',
        },
        {
          title: 'Constructor de Reportes Personalizados',
          body: 'Los equipos operativos configuran datasets, filtros, agrupaciones, cron y exportaciones para analítica repetible.',
          detail:
            'Plantillas reutilizables y ejecucion programada permiten analitica consistente para liderazgo, calidad y cumplimiento.',
          imageAlt: 'Constructor de reportes con filtros, campos de agrupación y opciones de ejecución',
        },
      ],
      emptyPageBody: 'Próximamente.',
    },
    aiCore: {
      headerTitle: 'Funciones',
      headerBody: 'Todo lo que ejecuta Elen1 en un único sistema clínico nativo de IA.',
      videoSubtitle: 'La salud merece algo mejor',
      featuresTitle: 'Funciones de IA y Núcleo Clínico',
      featuresIntro:
        'Cada funcionalidad opera con contexto clínico compartido, inferencia en tiempo real y soporte de decisión integrado directamente en el flujo asistencial.',
      features: [
        {
          title: 'Centro de Comando Clínico',
          body:
            'El Centro de Comando ofrece una visión unificada y en vivo de flujo de pacientes, carga de profesionales, tareas pendientes, focos de riesgo y señales financieras de la operación.',
          detail:
            'En lugar de navegar por paneles desconectados, los equipos coordinan decisiones y ejecutan acciones desde un único entorno operativo.',
          highlights: [
            {
              title: 'Visibilidad Unificada',
              body: 'Integra agenda, atención clínica, estado de documentación y señales de facturación en una sola vista en tiempo real.',
            },
            {
              title: 'Flujo Orientado a la Acción',
              body: 'Cada alerta crítica se conecta con acciones inmediatas para resolver cuellos de botella sin cambiar de pantalla.',
            },
          ],
          image: '/newfeatures/dashboard.png',
          imageAlt: 'Dashboard de Elen1 con inteligencia operativa y clínica',
        },
        {
          title: 'Agente de IA Sentinel dentro del EMR',
          body:
            'Sentinel no es una herramienta externa. Vive dentro del EMR y evalúa continuamente el contexto de la historia, condiciones activas, medicación actual y eventos recientes.',
          detail:
            'El clínico recibe recomendaciones contextualizadas en el mismo punto donde revisa la ficha y documenta, sin romper su flujo de trabajo.',
          highlights: [
            {
              title: 'Guía con Contexto Real',
              body: 'Las sugerencias se basan en la línea temporal completa del paciente, no en datos aislados de una única visita.',
            },
            {
              title: 'IA Nativa del Flujo Clínico',
              body: 'Las recomendaciones aparecen directamente dentro de la historia clínica y de la toma de órdenes.',
            },
          ],
          image: '/newfeatures/sentinel-ai-agent-living-inside-emr.png',
          imageAlt: 'Panel del agente Sentinel integrado en el flujo del EMR',
        },
        {
          title: 'Agente de IA de Triage Automático',
          body:
            'El agente de triage clasifica automáticamente a los pacientes entrantes por urgencia, severidad sospechada y riesgo de deterioro antes de la evaluación médica completa.',
          detail:
            'Así, admisión, enfermería y urgencias pueden priorizar casos críticos de forma temprana y reducir escaladas evitables en sala de espera.',
          highlights: [
            {
              title: 'Detección Temprana de Gravedad',
              body: 'Identifica ingresos de alto riesgo con síntomas, antecedentes de ingreso, signos vitales y episodios previos.',
            },
            {
              title: 'Gestión Dinámica de Cola',
              body: 'Reordena prioridades en tiempo real conforme llega nueva información clínica.',
            },
          ],
          image: '/newfeatures/automatic-triage-ai-agent.png',
          imageAlt: 'Agente de triage automático priorizando pacientes por urgencia',
        },
        {
          title: 'Resumen de Paciente Autogenerado por IA',
          body:
            'La ficha del paciente incluye un resumen autogenerado que sintetiza historia longitudinal, diagnósticos activos, medicación, alergias, tareas pendientes y tendencias de riesgo.',
          detail:
            'El resultado es un briefing clínico breve pero completo para comprender el estado actual del paciente en segundos.',
          highlights: [
            {
              title: 'Compresión de Historia Clínica',
              body: 'Convierte expedientes extensos en una narrativa estructurada centrada en cambios recientes y prioridades clínicas.',
            },
            {
              title: 'Contexto Listo para Decidir',
              body: 'Destaca riesgos emergentes y puntos sin resolver para acelerar decisiones seguras en el punto de atención.',
            },
          ],
          image: '/newfeatures/patient-details-autogenerated-ai-summary.png',
          imageAlt: 'Ficha de paciente con resumen clínico generado por IA',
        },
        {
          title: 'Inteligencia de Resultados de Laboratorio',
          body:
            'A medida que llegan los resultados, Elen1 interpreta laboratorios en contexto clínico y no como valores aislados, destacando trayectorias anómalas y combinaciones relevantes.',
          detail:
            'El equipo clínico visualiza qué cambió, por qué importa ahora y qué seguimiento se sugiere, sin revisar módulos por separado.',
          highlights: [
            {
              title: 'Interpretación con Tendencia',
              body: 'Detecta desviaciones progresivas y diferencia cambios agudos de variaciones esperadas.',
            },
            {
              title: 'Contexto Clínico Inmediato',
              body: 'Relaciona hallazgos de laboratorio con diagnósticos activos, tratamientos y eventos recientes.',
            },
          ],
          image: '/newfeatures/lab-results.png',
          imageAlt: 'Módulo de laboratorios con interpretación de IA y detección de tendencias',
        },
        {
          title: 'Espacio de Trabajo de Telemedicina',
          body:
            'El espacio de telemedicina unifica videollamada, acceso al expediente, órdenes y documentación en una misma interfaz diseñada para atención remota.',
          detail:
            'El profesional puede conducir y cerrar la consulta sin alternar herramientas, mejorando calidad asistencial y eficiencia administrativa.',
          highlights: [
            {
              title: 'Flujo Virtual de Extremo a Extremo',
              body: 'Consulta, revisión clínica, documentación y manejo de órdenes se completan en una sola superficie de trabajo.',
            },
            {
              title: 'Menor Fricción Cognitiva',
              body: 'Reduce cambios de aplicación durante la visita para mantener el foco clínico en el paciente.',
            },
          ],
          image: '/newfeatures/telemedicine.png',
          imageAlt: 'Flujo de telemedicina integrado con historia clínica del paciente',
        },
        {
          title: 'Diagnóstico en Vivo durante Telemedicina',
          body:
            'Durante la videoconferencia, Elen1 genera apoyo diagnóstico en tiempo real y resúmenes evolutivos de la consulta conforme aparece nueva información clínica.',
          detail:
            'Esto permite validar hipótesis, documentar hallazgos y cerrar decisiones mientras la atención todavía está en curso.',
          highlights: [
            {
              title: 'Soporte Clínico Durante la Consulta',
              body: 'Propone diagnósticos diferenciales estructurados y pasos de seguimiento en tiempo real.',
            },
            {
              title: 'Documentación Concurrente',
              body: 'Construye el resumen de visita en paralelo para que la historia quede casi completa al finalizar la llamada.',
            },
          ],
          image: '/newfeatures/telemedicine-ai-summary-live-diagnostics.png',
          imageAlt: 'Consulta de telemedicina con diagnóstico y resumen de IA en vivo',
        },
      ],
      contactTitle: 'Ve el Núcleo en Acción',
      contactBody: 'Whitepapers técnicos completos y documentación API disponibles bajo solicitud.',
      contactEmailPlaceholder: 'Correo institucional',
      contactSubmit: 'Solicitar Briefing Técnico',
    },
    investors: {
      heroLine1: 'Invierte en la',
      heroHighlight: 'Próxima Etapa',
      heroLine3: 'del software clínico.',
      heroSubtitle:
        'Elen1 está construyendo software para clínicas que mantiene agenda, acceso del paciente, facturación, telemedicina y operaciones clínicas en un solo sistema.',
      heroCta: 'Hablar con el equipo',
      heroImageAlt: 'Gráfico de Crecimiento y Panel de Agenda',
      shiftTitle: 'Una nueva etapa para el software clínico',
      feature1Title: 'Expansión regional',
      feature1Body:
        'Estamos construyendo para clínicas en Europa, Latinoamérica y Canadá con un producto que puede adaptarse a necesidades locales de operación y cumplimiento.',
      feature2Title: 'Profundidad de producto',
      feature2Body:
        'El producto se fortalece a medida que ampliamos cobertura de workflows, patrones de implementación más claros y mejor visibilidad operativa para las clínicas que acompañamos.',
      feature3Title: 'Preparación de cumplimiento',
      feature3Body:
        'Elen1 se está construyendo con expectativas reales de privacidad, seguridad y gobierno de IA para los mercados donde planeamos operar.',
      leadershipTitle: 'Equipo Directivo',
      leadershipSubtitle: 'Fundadores con experiencia comprobada en salud y fintech.',
      ceoRole: 'Fundador y CEO',
      ceoBio:
        'Emprendedor serial. Ex-Medesync y Telus Health. Lideró transformación digital para grandes bancos globales.',
      ctoRole: 'Co-Fundador y CTO',
      ctoBio:
        'Ex líder de ingeniería en PayPal. Arquitecto de infraestructura financiera segura y de alto volumen transaccional.',
      cooRole: 'Co-Fundador y COO',
      cooBio:
        'Ex ejecutivo de Medtronic e inversionista ángel destacado en Dubái. Fuerza estratégica para escalar salud global.',
      roundTitle: 'Habla con el equipo',
      roundBody: 'Estamos en conversaciones activas con inversionistas y socios estratégicos que entienden operaciones de salud y construcción de producto a largo plazo.',
      formFundPlaceholder: 'Fondo / Firma',
      formCheckSizePlaceholder: 'Interés de ticket',
      formEmailPlaceholder: 'Correo del socio',
      formSubmit: 'Iniciar la conversación',
    },
  },
  it: {
    layout: {
      navPlatform: 'Prodotto',
      navPatientPortal: 'Portale Paziente',
      navPatientApp: 'App Paziente',
      navEmrEhr: 'EMR / EHR',
      navAiCore: 'Funzionalità',
      navInvestors: 'Investitori',
      goToDashboard: 'Vai alla Dashboard',
      patientLogin: 'Portale Paziente',
      loginRequestAccess: 'Accedi',
      footer: '© 2026 Elen1 Systems. Dubai.',
    },
    platform: {
      heroLine1: 'Gli appuntamenti avanzano.',
      heroHighlight: 'La cura scorre.',
      heroLine3: 'Il team resta allineato.',
      heroSubtitle:
        'Elen1 trasforma agenda, accettazione, promemoria e passaggi di consegne in un ritmo operativo chiaro per cliniche sempre attive.',
      heroCta: 'Prenota una demo',
      heroImageAlt: 'Area agenda di Elen1 con calendario dei provider e dettagli degli appuntamenti',
      trustedLabel: 'Partnership di cui siamo orgogliosi',
      trustedBanner: 'PARTNER STRATEGICI IN ARRIVO',
      comparisonTitle: 'Gestire una clinica non dovrebbe essere cosi difficile',
      comparisonOldTitle: 'Il vecchio modo',
      comparisonOldSubtitle: 'Molti sistemi scollegati',
      comparisonOldItems: [
        'Strumenti che non comunicano tra loro',
        'Amministrazione manuale e rincorsa ai pazienti',
        'Poca visibilita su tutta la struttura',
        'La crescita aggiunge complessita, non controllo',
      ],
      comparisonNewTitle: 'Il modo Elen1',
      comparisonNewSubtitle: 'Una piattaforma per tutta la clinica',
      comparisonNewItems: [
        'Un posto per cura, operazioni e pagamenti',
        'Coinvolgimento del paziente collegato alla cartella',
        'Visibilita in tempo reale per tutto il team',
        'Scala senza aggiungere complessita',
      ],
      schedulingSectionEyebrow: 'Agenda e pagamenti',
      schedulingSectionTitle: 'Dalla prima prenotazione al pagamento finale, senza carico amministrativo',
      schedulingSectionBody:
        'Dal primo appuntamento al pagamento finale, ogni passaggio resta collegato in un unico flusso Elen1.',
      schedulingSectionItems: [
        'Prenotazione e riprogrammazione in pochi secondi dal portale paziente',
        "Moduli digitali e consensi inviati prima dell'arrivo",
        'Pagamenti, depositi e fatture gestiti in un solo posto',
        'La lista di attesa riempie automaticamente gli slot cancellati',
      ],
      schedulingSectionCta: 'Esplora agenda e pagamenti',
      schedulingSectionImageAlt: 'Anteprima del flusso prenotazioni e pagamenti di Elen1',
      operationsSectionEyebrow: 'Operazioni e team',
      operationsSectionTitle: 'Meno tempo sulla burocrazia. Piu tempo per i pazienti',
      operationsSectionBody:
        'Automatizza il lavoro ripetitivo cosi il team puo concentrarsi su cio che conta: i pazienti.',
      operationsSectionItems: [
        'Pazienti registrati e documentati in pochi minuti',
        'Template preconfigurati che riducono il tempo di documentazione',
        'Turni, commissioni e timesheet del personale in un solo posto',
        'Compliance e report per mantenere il controllo',
      ],
      operationsSectionCta: 'Esplora operazioni e team',
      operationsSectionImageAlt: 'Team clinico che revisiona le operazioni in Elen1',
      growthSectionEyebrow: 'Crescita e insight',
      growthSectionTitle: 'Piu pazienti, maggiore retention e piena visibilita sui ricavi',
      growthSectionBody:
        'Vedi esattamente come sta performando la clinica. Monitora ricavi, retention e attivita del team in una sola vista.',
      growthSectionItems: [
        'Report su ricavi, retention e KPI a colpo d occhio',
        'Membership e pacchetti che aumentano il valore del paziente',
        'Email, SMS e campagne nella stessa piattaforma',
        'Programmi loyalty e referral che riportano i pazienti',
      ],
      growthSectionCta: 'Esplora crescita e insight',
      growthSectionImageAlt: 'Anteprima dashboard crescita e insight clinica',
      aiScribeBrand: 'Elen1 AI scribe',
      aiScribeTitlePrefix: 'IA pensata',
      aiScribeTitleSuffix: 'per il lavoro quotidiano in clinica',
      aiScribeCta: 'Prova Elen1 AI',
      aiScribeKicker: 'Agente AI Scribe',
      aiScribeCardTitle: 'Trasforma la voce in note cliniche strutturate',
      aiScribeCardBody:
        'L IA ascolta durante la consultazione e genera note di trattamento e riepiloghi strutturati. Tu ti concentri sul paziente, non sullo schermo.',
      aiScribeListening: 'Elen1 AI sta prendendo appunti...',
      roiTitle: 'Progettato per ripagarsi',
      roiSource: 'Scenari illustrativi modellati su workflow clinici comuni, non risultati misurati di clienti. I risultati reali variano in base a dimensione, team, servizi e adozione.',
      roiStats: [
        {
          label: 'Riduci admin',
          title: 'Fino a ~40% meno tempo in amministrazione',
          body: 'Agenda, note cliniche, moduli, promemoria e follow-up passano attraverso meno passaggi scollegati.',
        },
        {
          label: 'Onboarding',
          title: 'Onboarding del team piu rapido',
          body: 'Il nuovo personale parte piu velocemente con workflow guidati, permessi chiari e un sistema operativo condiviso.',
        },
        {
          label: 'Valore paziente',
          title: 'Piu valore dai pazienti ricorrenti',
          body: 'Follow-up automatici, pacchetti, loyalty e rebooking aiutano a mantenere i pazienti coinvolti.',
        },
        {
          label: 'ROI',
          title: 'Pensato per coprire il proprio costo',
          body: 'Ridurre il lavoro manuale puo compensare il costo software prima di contare retention, ricavi e capacita.',
        },
      ],
      customerStoriesEyebrow: 'Cliniche fondatrici',
      customerStoriesTitle: 'Diventa una delle nostre cliniche fondatrici',
      customerStoriesBody:
        'Stiamo inserendo il nostro primo gruppo di cliniche e costruiamo Elen1 attorno al loro lavoro quotidiano reale.',
      customerStoriesCta: 'Diventa clinica fondatrice',
      customerStoriesPoints: [
        'Lavora direttamente con il team fondatore',
        'Onboarding e migrazione dati assistiti',
        'Influenza la roadmap per la tua specialita',
        'Accesso anticipato alle nuove funzioni',
      ],
      integrationsEyebrow: 'Integrazioni',
      integrationsTitle: 'Progettato per collegarsi agli strumenti che usi',
      integrationsBody:
        'Elen1 e progettato per collegarsi alle piattaforme che le cliniche usano per pagamenti, calendari, messaggistica e report. Le integrazioni arrivano nel tempo: dicci quali contano di piu per te.',
      integrationsCta: 'Parliamo di integrazioni',
      practiceTypesTitlePrefix: 'Esplora Elen1',
      practiceTypesTitleSuffix: 'per tipo di clinica...',
      practiceTypesCta: 'Vedi specialita',
      practiceTypes: [
        'Med Spa',
        'Fisioterapia',
        'Wellness',
        'Medicina generale',
        'Dermatologia',
        'Clinica IV Drip',
        'Clinica della pelle',
        'Trapianto capelli',
        'Chirurgia estetica',
        'Cliniche longevita',
        'Clinica laser',
        'Salute mentale',
      ],
      capabilitiesTitlePrefix: '...oppure esplora tutto cio che',
      capabilitiesTitleHighlight: 'Elen1 puo fare',
      capabilityGroups: [
        {
          title: 'Agenda e prenotazioni',
          items: ['Prenotazione online', 'Calendario', 'Classi', 'Lista di attesa', 'App mobile'],
        },
        {
          title: 'Cura e documentazione',
          items: ['Moduli digitali', 'Labs', 'Prescrizioni', 'Foto prima e dopo', 'Telehealth'],
        },
        {
          title: 'Pagamenti e operations',
          items: ['Elen1 Pay', 'Preventivi', 'Gestione team', 'Commissioni', 'Inventario e ordini'],
        },
        {
          title: 'Marketing e crescita',
          items: ['Campagne', 'Gestione lead', 'Loyalty e premi', 'Membership e pacchetti', 'Report e analytics'],
        },
      ],
      twinTitleLine1: 'Un solo spazio.',
      twinTitleLine2: 'Pensato per cliniche reali.',
      twinBody:
        'Elen1 unisce documentazione, agenda, comunicazione con il paziente, pagamenti e coordinamento operativo in un unico flusso, così il team può lavorare più velocemente senza rincorrere sistemi scollegati.',
      copilotTitle: 'Documentazione al passo con la cura',
      copilotBody: 'Raccoglie contesto clinico, riepiloghi della visita e azioni di follow-up direttamente nel flusso quotidiano del professionista.',
      riskEngineTitle: 'Chiarezza operativa in tutta la struttura',
      riskEngineBody: 'Mostra cosa è in programma, in sospeso, in ritardo o bloccato tra professionisti, stanze e percorsi del paziente in un solo posto.',
      twinImageAlt: 'Motore Predittivo di Rischio',
      genomicsTitleLine1: 'Intelligenza del Flusso Clinico',
      genomicsTitleLine2: 'per la Cura Quotidiana.',
      genomicsBody:
        'Riunisce storia del paziente, contesto farmacologico e segnali di rischio così il team può decidere meglio senza cercare informazioni in sistemi separati.',
      genomicsCta: 'Vedi il supporto clinico',
      genomicsImageAlt: 'Dashboard Genomica',
      architectureTitle: 'Perché le cliniche scelgono Elen1',
      features: [
        {
          title: 'Copilota Clinico IA in Ogni Cartella',
          body: 'Le cartelle includono riepiloghi longitudinali IA, fattori di rischio, care gap e follow-up suggeriti direttamente nel workflow clinico.',
        },
        {
          title: 'Accettazione Intelligente e Triage Urgenze',
          body: 'Le code walk-in usano punteggi di urgenza IA e segnali red-flag per prioritizzare arrivi e standardizzare le azioni successive.',
        },
        {
          title: 'Messaggistica Team con Contesto Paziente',
          body: 'La messaggistica stile inbox supporta cartelle, tagging pazienti e coordinamento per thread collegato al contesto clinico attivo.',
        },
        {
          title: 'Intelligence di Laboratorio (Compatibile HL7)',
          body: 'I workflow laboratorio integrano risultati HL7/ORU, evidenziano valori anomali e supportano azioni di acknowledgement tracciabili.',
        },
        {
          title: 'Monitoraggio Rischio Popolazionale e Remoto',
          body: 'Dashboard di population health e monitoraggio remoto mostrano segnali di rischio in tempo reale, deviazioni e azioni consigliate.',
        },
        {
          title: 'Analytics Operative e Fatturazione Integrata',
          body: 'Report personalizzati, KPI operativi e workspace di billing collegano visibilita clinica ed esecuzione del ciclo ricavi.',
        },
      ],
      doctorsTitleLine1: 'Riprendi l’',
      doctorsTitleLine2: 'Arte della Medicina.',
      doctorsBody:
        'Elen1 automatizza la burocrazia della cura. Dall’orchestrazione assistenziale con IA alla diagnosi differenziale con IA, noi gestiamo i dati così tu gestisci il paziente.',
      doctorsImageAlt: 'Bacheca Agenda',
      contactTitle: 'Unisciti alla Rete di Intelligenza',
      contactBody:
        'Elen1 è in fase di adozione in selezionati centri accademici.\nContattaci per partnership.',
      formNamePlaceholder: 'Nome',
      formOrgPlaceholder: 'Sistema Sanitario / Organizzazione',
      formEmailPlaceholder: 'Email',
      formSubmit: 'Richiedi Demo',
      patientPortalTitle: 'Portale Paziente',
      patientPortalSubtitle: 'Un unico spazio per appuntamenti, referti, piani e comunicazione sicura.',
      patientPortalIntro:
        'Il portale è progettato per i flussi quotidiani del paziente: prossime cure, follow-up, documenti, visibilità di fatturazione e coordinamento diretto con il team clinico.',
      patientPortalHighlights: [
        {
          title: 'Accesso Self-Service',
          body: 'I pazienti possono consultare attività, moduli e documentazione senza chiamare il front desk per aggiornamenti di routine.',
        },
        {
          title: 'Chiarezza Operativa',
          body: 'Appuntamenti, importi in sospeso e terapie attive sono mostrati in un’unica area di lavoro semplice.',
        },
        {
          title: 'Continuità Clinica',
          body: 'Referti di laboratorio e avanzamento dei piani di cura restano sempre visibili per allineare gli obiettivi clinici.',
        },
      ],
      patientPortalShowcase: [
        {
          title: 'Dashboard Home',
          body: 'Uno spazio riepilogativo con visite imminenti, moduli pendenti, farmaci attivi, report di laboratorio e attività di cura.',
          detail:
            'Il paziente vede subito cosa richiede azione oggi, incluse attivita amministrative e preparazione alla prossima visita.',
          imageAlt: 'Dashboard del portale paziente con attività e riepilogo appuntamenti',
        },
        {
          title: 'Centro Appuntamenti',
          body: 'I pazienti seguono visite imminenti e recenti, verificano lo stato e usano azioni rapide come accesso alla visita virtuale o messaggistica.',
          detail:
            'Stati e azioni rapide riducono i no-show mostrando orario, professionista e passo successivo in modo immediato.',
          imageAlt: 'Elenco appuntamenti del portale paziente con pannello azioni',
        },
        {
          title: 'Vista Referti di Laboratorio',
          body: 'I pannelli recenti sono organizzati per data di raccolta, medico richiedente e valori anomali per facilitare il follow-up.',
          detail:
            'I valori critici sono visibili a colpo d occhio, facilitando confronto clinico e tempi di risposta piu rapidi.',
          imageAlt: 'Tabella referti del portale paziente con segnalazioni di anomalie',
        },
        {
          title: 'Monitoraggio Piano di Cura',
          body: 'Barre di avanzamento e date della prossima revisione aiutano il paziente a capire aderenza e milestone cliniche.',
          detail:
            'La visione longitudinale del piano sostiene l aderenza terapeutica tra una visita e l altra.',
          imageAlt: 'Schede piani di cura del portale paziente con indicatori di avanzamento',
        },
      ],
      patientAppTitle: 'App Paziente',
      patientAppSubtitle: 'Applicazione mobile in arrivo per pazienti e famiglie.',
      patientAppBadge: 'App iOS In Arrivo',
      patientAppIntro:
        'La prossima app paziente porta l esperienza del portale su mobile con notifiche in tempo reale, follow-up piu semplice e accesso continuo ai percorsi di cura.',
      patientAppImageAlt: 'Anteprima della prossima app mobile paziente Elen1',
      patientAppLaunchEyebrow: 'App iOS per Pazienti',
      patientAppLaunchTitle: 'In Arrivo',
      patientAppLaunchDate: 'Accesso anticipato nel 2026',
      patientAppLaunchPrimaryCta: 'Unisciti alla waitlist',
      patientAppLaunchSecondaryCta: 'Vedi funzionalita',
      patientAppFeatures: [
        {
          title: 'Telemedicina su Mobile',
          body: 'Partecipa alle video visite programmate con un tocco e ricevi promemoria di check-in prima del consulto.',
        },
        {
          title: 'Messaggistica Sicura con il Team',
          body: 'Continua le conversazioni con medici e care coordinator direttamente dall app.',
        },
        {
          title: 'Referti e Cartella Clinica',
          body: 'Consulta nuovi referti, storico clinico e aggiornamenti del piano senza attendere richiamate telefoniche.',
        },
        {
          title: 'Appuntamenti e Promemoria Smart',
          body: 'Monitora visite imminenti, attivita preparatorie e cambi di stato tramite notifiche push.',
        },
        {
          title: 'Terapie e Task di Cura',
          body: 'Segui terapie, task quotidiani e milestone del piano in una timeline semplice orientata all aderenza.',
        },
        {
          title: 'Billing e Documenti',
          body: 'Accedi a fatture, stato pagamenti e moduli richiesti da un unico workspace mobile.',
        },
      ],
      emrEhrTitle: 'EMR / EHR',
      emrEhrSubtitle:
        'Un sistema operativo clinico completo per cura, triage, diagnostica, comunicazione e processi economici.',
      emrEhrIntro:
        'Lo spazio EMR/EHR unifica intake, workflow clinico, supporto decisionale con IA, comunicazione con i pazienti, laboratorio, monitoraggio remoto e fatturazione in un’unica piattaforma connessa.',
      emrEhrHighlights: [
        {
          title: 'Workflow Clinico End-to-End',
          body: 'Dalla registrazione del paziente al follow-up e alla fatturazione, i team lavorano nello stesso ambiente operativo.',
        },
        {
          title: 'Operatività Assistita da IA',
          body: 'Segnali di rischio, riepiloghi di triage e contesto clinico longitudinale sono integrati nel lavoro quotidiano.',
        },
        {
          title: 'Visibilità Trasversale',
          body: 'Operations, population health, monitoraggio remoto e report restano allineati per ridurre punti ciechi.',
        },
      ],
      emrEhrShowcase: [
        {
          title: 'Dashboard Operativa',
          body: 'La direzione visualizza riepiloghi IA sullo stato della clinica, trend KPI e carico appuntamenti per la gestione giornaliera.',
          detail:
            'La vista esecutiva intercetta attriti operativi in anticipo, supportando riallocazione rapida di agenda e risorse.',
          imageAlt: 'Dashboard operativa EMR con riepilogo IA, KPI e grafici di trend',
        },
        {
          title: 'Registro Pazienti',
          body: 'L’elenco pazienti supporta filtri, gestione stati, assegnazione dei referenti e azioni rapide in una singola tabella.',
          detail:
            'Filtri clinici e operativi aiutano i team a prioritizzare attivita di follow-up e workload quotidiano.',
          imageAlt: 'Tabella pazienti con filtri, stati e azioni rapide',
        },
        {
          title: 'Cartella Paziente con Riepilogo IA',
          body: 'Ogni cartella include un riepilogo longitudinale IA con fattori di rischio, care gaps, terapie e timeline clinica.',
          detail:
            'Il medico riceve un quadro sintetico ma completo di cambiamenti recenti, criticita aperte e rischi in evoluzione.',
          imageAlt: 'Cartella clinica con riepilogo IA longitudinale, rischi e timeline',
        },
        {
          title: 'Workspace di Messaggistica Clinica',
          body: 'La messaggistica sicura stile inbox consente cartelle, triage dei thread, tagging pazienti e coordinamento rapido del team.',
          detail:
            'Le conversazioni restano legate al contesto paziente, migliorando handoff e continuita tra unita assistenziali.',
          imageAlt: 'Inbox di messaggistica clinica con conversazione e paziente taggato',
        },
        {
          title: 'Triage Walk-In e Urgenza',
          body: 'Punteggi di urgenza e riepiloghi IA aiutano a prioritizzare gli arrivi e standardizzare le azioni successive in tempo reale.',
          detail:
            'Il triage guidato riduce variabilita in accettazione e accelera escalation sicure dei casi critici.',
          imageAlt: 'Coda urgenze con livelli di triage IA e raccomandazioni operative',
        },
        {
          title: 'Hub di Monitoraggio Remoto',
          body: 'I feed device in tempo reale evidenziano deviazioni, confidenza del segnale e azioni raccomandate per interventi proattivi.',
          detail:
            'Segnali ordinati per priorita trasformano dati remoti in workflow concreti di contatto e intervento precoce.',
          imageAlt: 'Tabella monitoraggio remoto con deviazioni biomarcatori e risk score',
        },
        {
          title: 'Workflow Referti di Laboratorio',
          body: 'Ingestione HL7, rilevazione valori anomali e passaggi di acknowledgement sono gestiti in un flusso clinico strutturato.',
          detail:
            'Inbox risultati, flag critici e tracciamento azioni abilitano un ciclo diagnostico verificabile end-to-end.',
          imageAlt: 'Modulo referti con inbox, flag di anomalia e dettaglio risultato',
        },
        {
          title: 'Workspace di Fatturazione',
          body: 'Creazione fatture, composizione voci, calcolo totali e saldo residuo sono gestiti direttamente dalla vista paziente.',
          detail:
            'Il contesto condiviso tra clinica e amministrazione riduce errori di addebito e accelera il ciclo di incasso.',
          imageAlt: 'Workspace fatturazione paziente con builder fattura e catalogo prestazioni',
        },
        {
          title: 'Intelligence di Population Health',
          body: 'I team monitorano alert, cluster di casi e distribuzione geografica per guidare interventi e capacità clinica.',
          detail:
            'La lettura geospaziale supporta campagne mirate e pianificazione capacitiva basata su rischio reale di popolazione.',
          imageAlt: 'Dashboard population health con mappa di distribuzione e metriche di alert',
        },
        {
          title: 'Builder Report Personalizzati',
          body: 'I team operations configurano dataset, filtri, raggruppamenti, schedule ed export per analisi ripetibili.',
          detail:
            'Template e automazioni schedulate rendono i report coerenti per governance, qualita e compliance.',
          imageAlt: 'Builder report personalizzati con filtri, campi di grouping ed esecuzione',
        },
      ],
      emptyPageBody: 'In arrivo.',
    },
    aiCore: {
      headerTitle: 'Funzionalità',
      headerBody: 'Tutto ciò che Elen1 esegue in un unico sistema clinico connesso.',
      videoSubtitle: 'La sanità merita di meglio',
      featuresTitle: 'Funzionalità IA e Core Clinico',
      featuresIntro:
        'Ogni funzionalità opera con contesto clinico condiviso, inferenza in tempo reale e supporto decisionale integrato direttamente nel flusso di cura.',
      features: [
        {
          title: 'Centro di Comando Clinico',
          body:
            'Il Centro di Comando fornisce una vista live e unificata su flusso pazienti, carico dei professionisti, attività pendenti, aree di rischio e indicatori finanziari operativi.',
          detail:
            'Anziché usare dashboard scollegate, i team coordinano decisioni e attivano interventi dallo stesso ambiente operativo.',
          highlights: [
            {
              title: 'Visibilità Unificata',
              body: 'Integra agenda, erogazione clinica, stato della documentazione e segnali di fatturazione in un’unica vista in tempo reale.',
            },
            {
              title: 'Flusso Orientato all’Azione',
              body: 'Ogni alert critico è collegato a step operativi immediati per risolvere colli di bottiglia senza cambiare schermata.',
            },
          ],
          image: '/newfeatures/dashboard.png',
          imageAlt: 'Dashboard Elen1 con intelligence operativa e clinica',
        },
        {
          title: 'Agente IA Sentinel dentro l’EMR',
          body:
            'Sentinel non è uno strumento esterno. Vive dentro l’EMR e valuta continuamente contesto clinico, condizioni attive, terapia in corso ed eventi recenti.',
          detail:
            'Il medico riceve suggerimenti contestualizzati direttamente nel punto in cui analizza la cartella e documenta la visita.',
          highlights: [
            {
              title: 'Guida Contestuale',
              body: 'Le raccomandazioni utilizzano l’intera timeline del paziente, non solo i dati della visita corrente.',
            },
            {
              title: 'IA Nativa del Workflow',
              body: 'I suggerimenti compaiono direttamente nelle aree di charting e order entry, riducendo interruzioni cognitive.',
            },
          ],
          image: '/newfeatures/sentinel-ai-agent-living-inside-emr.png',
          imageAlt: 'Pannello agente Sentinel integrato nel flusso EMR',
        },
        {
          title: 'Agente IA di Triage Automatico',
          body:
            'L’agente di triage classifica automaticamente i pazienti in ingresso per urgenza, severità sospetta e rischio di deterioramento prima della valutazione medica completa.',
          detail:
            'Questo permette a front-office, infermieri e team urgenti di accelerare i casi critici e ridurre escalation evitabili in sala d’attesa.',
          highlights: [
            {
              title: 'Rilevazione Precoce della Gravità',
              body: 'Individua rapidamente pazienti ad alto rischio usando sintomi, anamnesi iniziale, parametri vitali ed episodi precedenti.',
            },
            {
              title: 'Gestione Dinamica della Coda',
              body: 'Ricalcola le priorità in tempo reale man mano che arrivano nuovi dati clinici.',
            },
          ],
          image: '/newfeatures/automatic-triage-ai-agent.png',
          imageAlt: 'Agente di triage automatico che priorizza i pazienti per urgenza',
        },
        {
          title: 'Riepilogo Paziente Autogenerato da IA',
          body:
            'La scheda paziente include un riepilogo generato automaticamente che sintetizza storia longitudinale, diagnosi attive, farmaci, allergie, attività aperte e trend di rischio.',
          detail:
            'Il risultato è un briefing clinico sintetico ma completo, utile per comprendere rapidamente lo stato attuale del paziente.',
          highlights: [
            {
              title: 'Compressione della Timeline Clinica',
              body: 'Trasforma cartelle estese in una narrazione strutturata focalizzata su cambiamenti recenti e priorità assistenziali.',
            },
            {
              title: 'Contesto Pronto alla Decisione',
              body: 'Evidenzia rischi emergenti e punti clinici irrisolti per accelerare decisioni sicure al point of care.',
            },
          ],
          image: '/newfeatures/patient-details-autogenerated-ai-summary.png',
          imageAlt: 'Scheda paziente con riepilogo clinico autogenerato da IA',
        },
        {
          title: 'Intelligenza sui Risultati di Laboratorio',
          body:
            'Quando i referti arrivano, Elen1 interpreta i valori nel contesto clinico e non come numeri isolati, evidenziando traiettorie anomale e combinazioni rilevanti.',
          detail:
            'Il clinico vede subito cosa è cambiato, perché è rilevante ora e quali azioni di follow-up sono più appropriate.',
          highlights: [
            {
              title: 'Interpretazione Basata sui Trend',
              body: 'Rileva derive nel tempo e distingue variazioni attese da cambiamenti acuti clinicamente significativi.',
            },
            {
              title: 'Contesto Clinico Immediato',
              body: 'Collega anomalie di laboratorio a diagnosi attive, terapie in corso ed eventi recenti per ridurre segnali mancati.',
            },
          ],
          image: '/newfeatures/lab-results.png',
          imageAlt: 'Modulo risultati di laboratorio con interpretazione IA e trend anomali',
        },
        {
          title: 'Workspace di Telemedicina',
          body:
            'Il workspace di telemedicina unisce visita video, accesso alla cartella, inserimento ordini e documentazione in un’unica interfaccia pensata per la cura remota.',
          detail:
            'Il professionista gestisce e conclude la visita senza passare tra strumenti diversi, migliorando qualità clinica ed efficienza operativa.',
          highlights: [
            {
              title: 'Workflow Virtuale End-to-End',
              body: 'Consulto, revisione clinica, documentazione e gestione ordini vengono completati nello stesso spazio di lavoro.',
            },
            {
              title: 'Minore Attrito Cognitivo',
              body: 'Riduce il context switching durante la visita, mantenendo l’attenzione sul paziente e sulla qualità decisionale.',
            },
          ],
          image: '/newfeatures/telemedicine.png',
          imageAlt: 'Flusso di telemedicina integrato con cartella clinica del paziente',
        },
        {
          title: 'Diagnostica Live in Telemedicina',
          body:
            'Durante la video visita, Elen1 genera supporto diagnostico in tempo reale e riepiloghi progressivi man mano che emergono nuovi elementi clinici.',
          detail:
            'In questo modo il medico può validare ipotesi, documentare evidenze e finalizzare decisioni mentre il consulto è ancora attivo.',
          highlights: [
            {
              title: 'Supporto Clinico Durante la Visita',
              body: 'Propone diagnosi differenziali strutturate e possibili step successivi mentre la conversazione procede.',
            },
            {
              title: 'Documentazione in Parallelo',
              body: 'Compila il riepilogo clinico in tempo reale, lasciando la cartella quasi completa al termine della chiamata.',
            },
          ],
          image: '/newfeatures/telemedicine-ai-summary-live-diagnostics.png',
          imageAlt: 'Sessione di telemedicina con diagnostica live e riepilogo IA',
        },
      ],
      contactTitle: 'Vedi il Core in Azione',
      contactBody: 'Whitepaper tecnici completi e documentazione API disponibili su richiesta.',
      contactEmailPlaceholder: 'Email istituzionale',
      contactSubmit: 'Richiedi Briefing Tecnico',
    },
    investors: {
      heroLine1: 'Investi nell’',
      heroHighlight: 'Prossima Era',
      heroLine3: 'del software per cliniche.',
      heroSubtitle:
        'Elen1 sta costruendo software per cliniche che tiene agenda, accesso paziente, fatturazione, telemedicina e operazioni cliniche nello stesso sistema.',
      heroCta: 'Parla con il team',
      heroImageAlt: 'Grafico di Crescita e Bacheca Agenda',
      shiftTitle: 'Una nuova fase per il software clinico',
      feature1Title: 'Espansione regionale',
      feature1Body:
        'Stiamo costruendo per cliniche in Europa, America Latina e Canada con un prodotto capace di adattarsi a esigenze locali di operatività e compliance.',
      feature2Title: 'Profondità di prodotto',
      feature2Body:
        'Il prodotto diventa più forte man mano che ampliamo la copertura dei workflow, i modelli di implementazione e la visibilità operativa per le cliniche che supportiamo.',
      feature3Title: 'Preparazione alla compliance',
      feature3Body:
        'Elen1 viene sviluppato tenendo conto di aspettative reali su privacy, sicurezza e governance dell’IA nei mercati in cui prevediamo di operare.',
      leadershipTitle: 'Leadership Team',
      leadershipSubtitle: 'Founder esperti con track record in healthcare e fintech.',
      ceoRole: 'Founder & CEO',
      ceoBio:
        'Imprenditore seriale. Ex Medesync e Telus Health. Ha guidato trasformazioni digitali per grandi banche globali.',
      ctoRole: 'Co-Founder & CTO',
      ctoBio:
        'Ex Engineering Lead in PayPal. Architetto di infrastrutture finanziarie sicure ad alto volume transazionale.',
      cooRole: 'Co-Founder & COO',
      cooBio:
        'Ex Executive Medtronic e noto angel investor a Dubai. Forza strategica per la scalabilità globale in sanità.',
      roundTitle: 'Parla con il team',
      roundBody: 'Siamo in conversazioni attive con investitori e partner strategici che conoscono le operazioni sanitarie e la costruzione di prodotto nel lungo periodo.',
      formFundPlaceholder: 'Fondo / Società',
      formCheckSizePlaceholder: 'Interesse ticket',
      formEmailPlaceholder: 'Email partner',
      formSubmit: 'Avvia la conversazione',
    },
  },
} as const;

export type LandingLocale = keyof typeof landingCopy;

/* The public landing pages only ship English and Spanish; other app languages
   (e.g. the dashboard's Italian) must not leak into landing content lookups. */
export type LandingPublicLocale = Extract<LandingLocale, 'en' | 'es'>;

export function resolveLandingLocale(language: string | undefined): LandingPublicLocale {
  if (language === 'es') return 'es';
  return 'en';
}
